import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  AUTH_STORAGE_KEY,
  clearStoredAuthStatus,
  getStoredAuthStatus,
  persistAuthStatus,
  validateCredentials,
} from "../utils";

const AuthContext = createContext({
  isAuthenticated: false,
  login: async () => false,
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => getStoredAuthStatus());

  useEffect(() => {
    setIsAuthenticated(getStoredAuthStatus());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleStorage = (event) => {
      if (event.key === null || event.key === AUTH_STORAGE_KEY) {
        setIsAuthenticated(getStoredAuthStatus());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const login = useCallback(async (username, password) => {
    const isValid = validateCredentials(username, password);
    if (isValid) {
      persistAuthStatus(true);
      setIsAuthenticated(true);
      return true;
    }
    clearStoredAuthStatus();
    setIsAuthenticated(false);
    return false;
  }, []);

  const logout = useCallback(() => {
    clearStoredAuthStatus();
    setIsAuthenticated(false);
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      login,
      logout,
    }),
    [isAuthenticated, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
